import Axios from 'axios';
import { matchPath } from 'react-router';
import { call, put, takeLatest } from 'redux-saga/effects';

import { restClient } from 'config/store/configureStore';
import { PATH } from 'constants/RoutesConstants';
import { createNotification } from 'modules/toast-notifications/actions';
import { getDefaultHeaders } from 'utils/RequestUtils';

import {
  END_REQUEST,
  FAILURE_REQUEST,
  SUCCESS_REQUEST,
  FLEETBACK_API_ERROR,
  DEFAULT_FAILURE_MESSAGE
} from './constants';

/**
 * @param {string} str
 * @return {string}
 */
export const extractUUIDParams = str => {
  if (str) {
    const uuidRegex = /[0-9a-fA-F]{8}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{12}/g;
    const matches = Array.from(str.matchAll(uuidRegex));
    if (matches.length > 0) {
      const match = matches.pop();
      return match[0];
    }
  }

  return undefined;
};

/**
 * @param {import('modules/global/generic.types').FleetbackApiError} error
 * @return boolean
 */
export const isNotFoundEntityError = (error = {}) =>
  error.errorCode === 404 && error.errorStatus === FLEETBACK_API_ERROR.NOT_FOUND;

/**
 * @param {import('modules/global/generic.types').Action<import('axios').AxiosError<import('modules/global/generic.types').FleetbackApiError>>} action
 * @returns {import('axios').AxiosError}
 */
export const getActionPayloadError = action =>
  // The old actions doesn't respect the "Flux Standard Actions" convention, so the error could be everywhere
  Object.values(action).find(value => value instanceof Error) ?? action.payload?.message;

/**
 * @param {string} route
 * @param {Array<import('constants/RoutesConstants').FBPath>} blacklist
 * @returns {import('constants/RoutesConstants').FBPath}
 * @description
 *  Try to determinate the parent sub path
 *  Examples:
 *      For the given route /intervention/:id, the function will return /intervention
 *      For the given route /dealers/update/:id, the function will return /dealers, because the route /dealers/update is not defined
 */
export const getParentPath = (route, blacklist = []) => {
  const parentPath = Object.values(PATH).find(config => config.path === route || config.simplePath === route);
  if (parentPath && !blacklist.includes(parentPath)) {
    return { path: parentPath.simplePath ?? parentPath.path };
  }

  const subRoute = route.substring(0, route.lastIndexOf('/'));
  if (subRoute.length > 0) {
    return getParentPath(subRoute, blacklist);
  }
  return undefined;
};

/**
 * @param {import('constants/RoutesConstants').FBPath} config
 * @param {string} pathname
 * @returns {import('react-router').PathMatch<string>}
 */
const matchAllPath = (config, pathname) => {
  if (pathname) {
    const key = config.path.match(/:/gi)?.length > 1 ? 'simplePath' : 'path';
    return matchPath(config?.[key] ?? config.path, pathname);
  }

  return undefined;
};

/**
 * @param {string} pathname
 * @returns {string}
 */
export const determinateId = pathname => {
  const route = Object.values(PATH).find(config => matchAllPath(config, pathname));
  if (route) {
    const match = matchAllPath(route, pathname);
    if (match) {
      const [entityId] = Object.values(match.params);
      return entityId;
    }
  }
  return undefined;
};

/** @param {import('modules/global/generic.types').LocationChangeAction} action */
export const isErrorLocationChange = ({ payload }) =>
  matchPath(PATH.ERROR_404.path, payload.location.pathname) ||
  matchPath(PATH.ERROR_GLOBAL.path, payload.location.pathname);

/**
 * @param {import('modules/global/generic.types').Action<unknown>} action
 * @returns {boolean}
 */
export const isAction = action => typeof action === 'object' && typeof action.type === 'string';

/**
 * @param {string} type
 * @returns {boolean}
 */
export const isRequestActionType = type => typeof type === 'string' && type.endsWith(END_REQUEST);

/**
 * @param {import('modules/global/generic.types').Request<unknown, unknown>} request
 * @returns {boolean}
 */
export const hasRequestFormat = request => typeof request?.url === 'string' && typeof request.method == 'string';

/**
 * @param {import('modules/global/generic.types').ActionRequest<unknown>} action
 * @returns {boolean}
 */
export const isActionRequest = action =>
  isAction(action) && isRequestActionType(action.type) && hasRequestFormat(action.payload.request);

/**
 * @param {string} type
 * @returns {string}
 * @throws {Error} The given type should be a request type - suffix should be REQUEST
 */
export const getBaseActionType = type => {
  if (!isRequestActionType(type)) {
    throw new Error(`Given type (${type}) does not match a request action type`);
  }
  return type.substring(0, type.length - END_REQUEST.length);
};

/**
 * @param {unknown} data
 * @param {Object} _ - Action meta
 * @param {import('modules/global/generic.types').ActionRequest<unknown>} sourceAction
 * @returns {import('modules/global/generic.types').Action<unknown>}
 */
export const generateSuccessCallback = (data, _, sourceAction) => {
  const baseType = getBaseActionType(sourceAction.type);
  return {
    type: `${baseType}${SUCCESS_REQUEST}`,
    payload: data,
    meta: {
      ...(sourceAction.meta ?? {}),
      '@app-auto-formatted': true,
    },
  };
};

/**
 * @param {import('modules/global/generic.types').ApiError} error - The error caught on the
 *   `httpClient.request` call (Axios error; NOT a message string)
 * @param {Object} _ - Action meta
 * @param {import('modules/global/generic.types').ActionRequest<unknown>} sourceAction
 * @returns {import('modules/global/generic.types').ApiFailureAction}
 */
export const generateFailureCallback = (error, _, sourceAction) => {
  const baseType = getBaseActionType(sourceAction.type);
  return {
    type: `${baseType}${FAILURE_REQUEST}`,
    payload: error,
    error: true,
    meta: {
      ...(sourceAction.meta ?? {}),
      '@app-auto-formatted': true,
    },
  };
};

/**
 * @param {'success' | 'failure'} type
 * @param {import('./types').SimpleRequestConfig} config
 */
export const getCallbackAction = (type, config) => {
  if (type !== 'success' && type !== 'failure') {
    throw new Error('Invalid type');
  }
  return config?.callbacks?.[type] ?? (type === 'success' ? generateSuccessCallback : generateFailureCallback);
};

/**
 * @param {'success' | 'failure'} type
 * @param {import('./types').SimpleRequestConfig} config
 * @param {import('modules/global/generic.types').Action<unknown>} successAction
 * @param {import('modules/global/generic.types').ActionRequest<unknown>} baseAction
 * @returns
 */
export function getNotificationMsgDescriptor(type, config, successAction, baseAction) {
  if (type !== 'success' && type !== 'failure') {
    throw new Error('Invalid type');
  }

  const notificationsConfig = config?.notifications?.[type];
  const isDefined = Boolean(notificationsConfig);
  if (isDefined) {
    const isFn = typeof notificationsConfig === 'function';
    return isFn ? notificationsConfig(successAction, baseAction) : notificationsConfig;
  }

  const method = baseAction?.payload?.request?.method?.toUpperCase();
  if (type === 'failure' && method !== 'HEAD') {
    return DEFAULT_FAILURE_MESSAGE;
  }

  return undefined;
}

/**
 * Core saga that executes an API request and handles success/failure automatically.
 * Used internally by simpleRequestsLoader but can also be called directly for custom sagas.
 *
 * @param  {[
 *  import('./types').SimpleRequestConfig,
 *  import('modules/global/generic.types').ActionRequest<unknown>
 * ]} args - Variable arguments: [config, action] or just [action]
 *
 * @yields {import('redux-saga/effects').CallEffect} HTTP request execution
 * @yields {import('redux-saga/effects').PutEffect} Success/failure action dispatch
 * @yields {import('redux-saga/effects').PutEffect} Optional notification dispatch
 *
 * @throws {Error} If action is not a valid request action (missing type or request format)
 *
 * @example
 * // Direct usage in custom saga
 * function* fetchWithLogging(action) {
 *   console.log('Starting fetch...');
 *   yield* simpleRequest(action);
 *   console.log('Fetch completed');
 * }
 *
 * @example
 * // With custom config
 * function* fetchWithConfig(action) {
 *   const config = {
 *     callbacks: {
 *       success: (data, meta) => customSuccessAction(data, meta),
 *     },
 *     notifications: {
 *       success: { id: 'custom.success', defaultMessage: 'Done!' },
 *     },
 *   };
 *   yield* simpleRequest(config, action);
 * }
 *
 * @description
 * Execution flow:
 * 1. Validates action format (must have type ending with _REQUEST and payload.request)
 * 2. Executes HTTP request via restClient (or Axios if @no-authentication meta)
 * 3. On SUCCESS:
 *    - Calls config.callbacks.success OR auto-generates success action
 *    - Dispatches the success action
 *    - Shows success notification if configured
 * 4. On FAILURE:
 *    - Calls config.callbacks.failure OR auto-generates failure action
 *    - Dispatches the failure action
 *    - Shows failure notification if configured
 *
 * Special meta flags:
 * - @app-request: true (REQUIRED) - Enables request middleware
 * - @no-authentication: true - Skip authentication headers
 * - @add-etag-in-result: true - Adds ETag header to response data
 */
export function* simpleRequest(...args) {
  let notification;
  let type = 'success';
  const [config] = args;
  const action = args.pop();

  if (!isActionRequest(action)) {
    throw new Error(`Given type (${action.type}) does not match a request action type`);
  }

  try {
    const skipAuthentication = action.meta?.['@no-authentication'];
    const addEtagData = action.meta?.['@add-etag-in-result'];
    const httpClient = skipAuthentication ? Axios : restClient;

    const { data, headers } = yield call(httpClient.request, {
      ...action.payload.request,
      headers: {
        ...action.payload.request.headers,
        ...getDefaultHeaders(),
      },
    });

    if (addEtagData && headers.etag) {
      data.etag = headers.etag;
    }

    const successAction = getCallbackAction('success', config)?.(data, action.meta, action);
    notification = getNotificationMsgDescriptor('success', config, successAction, action);
    yield put(successAction);
  } catch (error) {
    type = 'error';
    const failureAction = getCallbackAction('failure', config)?.(error, action.meta, action);
    yield put(failureAction);
    notification = getNotificationMsgDescriptor('failure', config, failureAction, action);
  }

  if (notification) {
    yield put(createNotification({ type, message: notification }));
  }
}

/**
 * Creates saga effects for multiple request actions with automatic handling of success/failure.
 * Simplifies saga creation by providing declarative configuration for API requests.
 *
 * @param {Record<string, import('./types').SimpleRequestConfig>} requestsConfig - Configuration object
 *   where keys are action types (e.g., FETCH_ENTITIES_REQUEST) and values are config objects
 *
 * @returns {Array<import('redux-saga/effects').ForkEffect>} Array of saga effects ready to be yielded
 *
 * @example
 * // Basic usage with auto-generated actions and notifications
 * const simpleRequests = {
 *   [types.CREATE_ENTITY]: {
 *     // Auto-generates CREATE_ENTITY_SUCCESS / CREATE_ENTITY_FAILURE
 *     // Auto-notification for POST: "Created successfully"
 *   },
 * };
 *
 * @example
 * // With custom callbacks to transform response
 * const simpleRequests = {
 *   [types.FETCH_ENTITIES]: {
 *     callbacks: {
 *       success: (data, meta, sourceAction) =>
 *         fetchEntitiesSuccess(sourceAction.payload.entityId, data, meta),
 *       failure: (error, meta) => fetchEntitiesFailure(error, meta),
 *     },
 *   },
 * };
 *
 * @example
 * // With custom notifications (static or dynamic)
 * const simpleRequests = {
 *   [types.IMPORT_ENTITY]: {
 *     notifications: {
 *       success: (successAction, sourceAction) => ({
 *         id: `import_${sourceAction.payload.type}.success`,
 *         defaultMessage: 'Import successful',
 *       }),
 *       failure: { id: 'import.failure', defaultMessage: 'Import failed' },
 *     },
 *   },
 * };
 *
 * @example
 * // With custom effect (takeEvery instead of default takeLatest)
 * const simpleRequests = {
 *   [types.LOG_EVENT]: {
 *     effect: takeEvery,
 *     notifications: { success: undefined, failure: undefined },
 *   },
 * };
 *
 * @example
 * // Complete saga with simpleRequestsLoader
 * export default function* mySaga() {
 *   yield all(simpleRequestsLoader(simpleRequests));
 * }
 *
 * @description
 * How it works:
 * 1. For each action type in requestsConfig, creates a saga effect (default: takeLatest)
 * 2. Each effect listens for the action type and calls simpleRequest with the config
 * 3. simpleRequest executes the HTTP request from action.payload.request
 * 4. On success: dispatches success action (custom or auto-generated) + optional notification
 * 5. On failure: dispatches failure action (custom or auto-generated) + optional notification
 *
 * Config options:
 * - callbacks.success: (data, meta, sourceAction) => Action - Custom success action creator
 * - callbacks.failure: (error, meta, sourceAction) => ErrorAction - Custom failure action creator
 * - notifications.success: MessageDescriptor | Function - Success notification
 * - notifications.failure: MessageDescriptor | Function - Failure notification
 * - effect: SagaEffect - Saga effect to use (default: takeLatest)
 *
 * Auto-generated actions:
 * - If no callbacks provided, auto-generates actions based on action type
 * - Example: FETCH_ENTITIES_REQUEST → FETCH_ENTITIES_SUCCESS / FETCH_ENTITIES_FAILURE
 *
 */
export const simpleRequestsLoader = requestsConfig =>
  Object.entries(requestsConfig).map(([actionType, config]) => {
    const effectFn = config?.effect ?? takeLatest;
    return effectFn(actionType, simpleRequest, config);
  });
